import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Target, Compass, Sparkles, Users } from "lucide-react";

export default function About() {
  const { t, lang } = useI18n();
  const [team, setTeam] = useState([]);
  useEffect(() => { api.get("/team").then(r => setTeam(
    (r.data || []).filter(m => m.is_active !== false && m.is_archived !== true)
  )).catch(() => {}); }, []);
  const values = [
    { icon: Target, key: "v1" },
    { icon: Compass, key: "v2" },
    { icon: Sparkles, key: "v3" },
    { icon: Users, key: "v4" },
  ];

  return (
    <div data-testid="about-page">
      <section className="container-x py-24">
        <span className="eyebrow">{t("about.heading")}</span>
        <h1 className="mt-4 text-4xl md:text-6xl text-navy leading-tight max-w-3xl">
          {lang === "ar"
            ? "نُصمّم الحوكمة على قياس تطلعات القادة"
            : "We tailor governance to the ambitions of leaders."}
        </h1>
        <span className="divider-gold" />
        <p className="max-w-3xl text-navy/70 leading-relaxed text-lg">
          {lang === "ar"
            ? "تأسست Hawkam على فكرة أن الحوكمة الرشيدة ليست عبئاً بل ركيزة نمو. نُقدّم استشارات تجمع بين الصرامة الأكاديمية والحس العملي."
            : "Hawkam was founded on the belief that governance is not a constraint but a growth catalyst — advisory that blends academic rigor with pragmatic craft."}
        </p>
      </section>

      <section className="bg-white py-24">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="card-elev p-10">
            <span className="eyebrow">{t("about.vision")}</span>
            <h2 className="mt-4 text-3xl text-navy">{t("about.visionBody")}</h2>
          </div>
          <div className="card-elev p-10">
            <span className="eyebrow">{t("about.mission")}</span>
            <h2 className="mt-4 text-3xl text-navy">{t("about.missionBody")}</h2>
          </div>
        </div>
      </section>

      <section className="container-x py-24">
        <span className="eyebrow">{t("about.values")}</span>
        <h2 className="mt-4 text-3xl md:text-4xl text-navy">
          {lang === "ar" ? "قيمنا الأربع" : "Our four values"}
        </h2>
        <span className="divider-gold" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {values.map(({ icon: I, key }) => (
            <div key={key} className="border border-navy/10 bg-white p-6 hover:-translate-y-1 transition-transform">
              <I size={24} className="text-gold" />
              <h3 className="mt-4 text-lg text-navy">{t(`about.${key}`)}</h3>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow">{t("about.team")}</span>
          <h2 className="mt-4 text-3xl md:text-4xl">
            {lang === "ar" ? "قيادة تجمع الخبرة والرؤية" : "Leadership across experience and vision"}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="team-grid">
            {team.length === 0 ? (
              <div className="md:col-span-3 border border-gold/20 bg-navy/60 backdrop-blur-md p-10 text-center text-cream/70" data-testid="team-empty">
                {lang === "ar"
                  ? "سيتم تحديث تفاصيل الفريق والشركاء قريباً"
                  : "Team details and partners will be updated soon."}
              </div>
            ) : team.map((m) => (
              <div key={m.id} className="border border-gold/20 bg-navy/60 backdrop-blur-md" data-testid={`team-card-${m.id}`}>
                {m.image_url ? (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={m.image_url} alt={m.name_en} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-gradient-to-br from-navy to-navy/80 flex items-center justify-center">
                    <span className="text-6xl text-gold/40 font-serif">{(m.name_en || "?").charAt(0)}</span>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl">{lang === "ar" ? m.name_ar : m.name_en}</h3>
                  <p className="mt-2 text-xs uppercase tracking-widest text-gold">{lang === "ar" ? m.role_ar : m.role_en}</p>
                  {(m.bio_ar || m.bio_en || m.description_ar || m.description_en) && (
                    <p className="mt-3 text-sm text-cream/80 leading-relaxed" data-testid={`team-bio-${m.id}`}>
                      {lang === "ar"
                        ? (m.bio_ar || m.description_ar || m.bio_en || m.description_en)
                        : (m.bio_en || m.description_en || m.bio_ar || m.description_ar)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
